import { api } from "@/api"
import { BASE_URL, ENDPOINTS } from "@/constants"
import { toast } from "@/hooks/use-toast"
import i18n from "@/i18n"
import { getApiErrorMessage } from "@/lib/api-error"
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@/libs"
import type {
  WebhookInboxDetail,
  WebhookInboxFilters,
  WebhookInboxPage,
  WebhookInboxSummary,
} from "@/types/webhook-inbox"

/** Chave base das queries do inbox — usada também na invalidação. */
export const WEBHOOK_INBOX_QUERY_KEY =
  ENDPOINTS.WEBHOOK_INBOX

/** Ordenação do inbox: recebimento mais recente primeiro (formato do Spring Data). */
const SORT_NEWEST_FIRST = "receivedAt,desc"

const buildQueryString = (filters: WebhookInboxFilters) => {
  const params = new URLSearchParams()
  params.set("page", String(filters.page))
  params.set("size", String(filters.size))
  // Mais recente primeiro: quem abre o inbox está atrás do que acabou de chegar. Sem isto a
  // ordem é a que o banco devolver, e ordenar no cliente só arrumaria a página exibida.
  params.set("sort", SORT_NEWEST_FIRST)
  if (filters.status) params.set("status", filters.status)
  if (filters.processor)
    params.set("processor", filters.processor)
  // `whitelabel_ids` é o parâmetro que o gateway valida contra os white labels do token
  // (jwt-services.validateWhiteLabelQuery) — mesmo nome usado pelos dashboards. O antigo
  // `whiteLabelId` era recusado pelo payment com 400 e, quando ausente, a consulta saía global e
  // a tela mostrava webhook de outros white labels.
  if (filters.whiteLabelId)
    params.set("whitelabel_ids", filters.whiteLabelId)
  if (filters.transactionId)
    params.set("transactionId", filters.transactionId)
  if (filters.startDate)
    params.set("startDate", filters.startDate)
  if (filters.endDate)
    params.set("endDate", filters.endDate)
  return params.toString()
}

/** Lista paginada dos webhooks recebidos (sem payload). */
export const getWebhookInbox = async (
  filters: WebhookInboxFilters
): Promise<WebhookInboxPage> => {
  const url = `${BASE_URL}${ENDPOINTS.WEBHOOK_INBOX}?${buildQueryString(filters)}`
  const { data } = await api.get<WebhookInboxPage>(url)
  return data
}

export const webhookInboxQueryOptions = (
  filters: WebhookInboxFilters
) => ({
  queryKey: [WEBHOOK_INBOX_QUERY_KEY, filters] as const,
  queryFn: async () => getWebhookInbox(filters),
  refetchOnMount: "always" as const,
  gcTime: 0,
  placeholderData: undefined,
})

export const useWebhookInbox = (
  filters: WebhookInboxFilters
) => useQuery(webhookInboxQueryOptions(filters))

/** Detalhe de um item, com payload cru e a transação correlacionada. */
export const getWebhookInboxById = async (
  id: string
): Promise<WebhookInboxDetail> => {
  const url = `${BASE_URL}${ENDPOINTS.WEBHOOK_INBOX}/${id}`
  const { data } = await api.get<WebhookInboxDetail>(url)
  return data
}

export const useWebhookInboxDetail = (id?: string) =>
  useQuery({
    queryKey: [WEBHOOK_INBOX_QUERY_KEY, "detail", id],
    queryFn: async () => getWebhookInboxById(id as string),
    enabled: Boolean(id),
    gcTime: 0,
  })

export const getWebhookInboxSummary =
  async (): Promise<WebhookInboxSummary> => {
    const url = `${BASE_URL}${ENDPOINTS.WEBHOOK_INBOX_SUMMARY}`
    const { data } = await api.get<WebhookInboxSummary>(url)
    return data
  }

export const useWebhookInboxSummary = () =>
  useQuery({
    queryKey: [WEBHOOK_INBOX_QUERY_KEY, "summary"],
    queryFn: getWebhookInboxSummary,
    gcTime: 0,
  })

/**
 * Reenfileira um webhook para novo processamento. O payment responde 202 quando
 * a mensagem entrou na fila e 503 quando o broker está fora — por isso o
 * sucesso aqui não é presumido pelo clique.
 */
export const useReprocessWebhookInboxMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<unknown, unknown, string>({
    mutationFn: async (id: string) => {
      const url = `${BASE_URL}${ENDPOINTS.WEBHOOK_INBOX}/${id}/reprocess`
      const { data } = await api.post(url)
      return data
    },
    onSuccess: async () => {
      toast({
        title: i18n.t(
          "pages.adminWebhookInbox.reprocessSuccess"
        ),
        variant: "success",
      })
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === WEBHOOK_INBOX_QUERY_KEY,
      })
    },
    onError: (error: unknown) => {
      toast({
        title: getApiErrorMessage(
          error,
          i18n.t("pages.adminWebhookInbox.reprocessError")
        ),
        variant: "destructive",
      })
    },
  })
}
